import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BellIcon, PaperClipIcon, EllipsisVerticalIcon, CheckIcon, PlusIcon, XMarkIcon,
  UserIcon, CalendarDaysIcon, DocumentTextIcon, BuildingOffice2Icon, ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { Tip } from '../ui/tip';
import { getImageUrl } from '../../utils/imagePaths';
import { etiquetaVence, etiquetaCompletada, claveGrupo } from '../../utils/tareasFechas';
import type { Assignee, Subtask, TaskItem, TaskMention } from './types';

/** Iniciales y color estable por id, para reconocer a cada persona de un vistazo */
export const Avatar: React.FC<{ id?: string | null; name?: string | null; size?: 'sm' | 'md' }> = ({ id, name, size = 'md' }) => {
  const label = String(name || '?').trim();
  const partes = label.split(/\s+/);
  const ini = ((partes[0]?.[0] || '') + (partes[1]?.[0] || partes[0]?.[1] || '')).toUpperCase();
  let h = 0;
  for (const ch of String(id || label)) h = (h * 31 + ch.charCodeAt(0)) % 360;
  const dim = size === 'sm' ? 'h-5 w-5 text-[9px]' : 'h-6 w-6 text-[10px]';
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white ${dim}`}
      style={{ backgroundColor: `hsl(${h} 38% 42%)` }}
    >
      {ini || '?'}
    </span>
  );
};

const ICONO_MENCION: Record<TaskMention['type'], React.ComponentType<{ className?: string }>> = {
  user: UserIcon,
  client: BuildingOffice2Icon,
  appointment: CalendarDaysIcon,
  budget: DocumentTextIcon
};

// A dónde lleva el vínculo de la tarea
export const rutaDeVinculo = (t: TaskItem): string | null => {
  if (!t.linkedType) return null;
  const id = t.linkedId || '';
  switch (t.linkedType) {
    case 'route': return id || null;
    case 'client': return id ? `/clients/${id}` : '/clients';
    case 'vehicle': return id ? `/vehicles/${id}/history` : null;
    case 'appointment': return `/appointments-enhanced${t.linkedLabel ? `?q=${encodeURIComponent(t.linkedLabel)}` : ''}`;
    case 'budget': return `/budgets${t.linkedLabel ? `?q=${encodeURIComponent(t.linkedLabel)}` : ''}`;
    case 'remito': return '/remitos?filter=draft';
    case 'product': return id ? `/products/${id}` : '/products';
    case 'provider': return id ? `/providers/${id}` : '/providers';
    case 'alert': return '/dashboard';
    default: return null;
  }
};

const rutaDeMencion = (m: TaskMention): string | null => {
  switch (m.type) {
    case 'client': return `/clients/${m.id}`;
    case 'appointment': return `/appointments-enhanced?q=${encodeURIComponent(m.label)}`;
    case 'budget': return `/budgets?q=${encodeURIComponent(m.label)}`;
    default: return null;
  }
};

const PRIORIDAD: Record<string, { cls: string; label: string }> = {
  urgent: { cls: 'bg-red-500', label: 'Urgente' },
  high: { cls: 'bg-amber-500', label: 'Alta' },
  normal: { cls: 'bg-muted-foreground/40', label: 'Normal' },
  low: { cls: 'bg-muted-foreground/25', label: 'Baja' }
};

const renderDetalle = (detail: string): React.ReactNode => {
  const lineas = detail.split('\n');
  const out: React.ReactNode[] = [];
  let lista: React.ReactNode[] | null = null;
  lineas.forEach((l, i) => {
    const b = l.match(/^\s*[-*]\s+(.*)$/);
    if (b) { if (!lista) lista = []; lista.push(<li key={`li-${i}`}>{b[1]}</li>); return; }
    if (lista) { out.push(<ul key={`ul-${i}`} className="my-1 ml-5 list-disc space-y-0.5">{lista}</ul>); lista = null; }
    if (l.trim()) out.push(<p key={`p-${i}`} className="my-1">{l}</p>);
  });
  if (lista) out.push(<ul key="ul-fin" className="my-1 ml-5 list-disc space-y-0.5">{lista}</ul>);
  return out;
};

interface Props {
  task: TaskItem;
  hoy: string;
  assignees: Assignee[];
  canAssign: boolean;
  currentUserId: string;
  expanded: boolean;
  onToggleExpand: (id: string) => void;
  onComplete: (task: TaskItem, done: boolean) => void;
  onPatch: (task: TaskItem, patch: Partial<TaskItem>) => Promise<void> | void;
  onPostpone: (task: TaskItem, cuando: 'manana' | 'lunes') => void;
  onDelete: (task: TaskItem) => void;
}

const TaskRow: React.FC<Props> = ({ task, hoy, assignees, canAssign, currentUserId, expanded, onToggleExpand, onComplete, onPatch, onPostpone, onDelete }) => {
  const navigate = useNavigate();
  const [nuevoSubpaso, setNuevoSubpaso] = useState('');
  const done = task.status === 'completed';
  const grupo = claveGrupo(task, hoy);
  const vence = done ? etiquetaCompletada(task, hoy) : etiquetaVence(task, hoy);
  const pri = PRIORIDAD[task.priority] || PRIORIDAD.normal;
  const subtasks = task.subtasks || [];
  const mentions = task.mentions || [];
  const ruta = rutaDeVinculo(task);
  const puedeEditar = canAssign || task.createdBy === currentUserId || task.assignedTo === currentUserId;

  const toggleSubpaso = (idx: number) => {
    const next = subtasks.map((s, i) => (i === idx ? { ...s, done: !s.done } : s));
    onPatch(task, { subtasks: next });
  };
  const agregarSubpaso = () => {
    const text = nuevoSubpaso.trim();
    if (!text) return;
    onPatch(task, { subtasks: [...subtasks, { text, done: false }] });
    setNuevoSubpaso('');
  };
  const quitarSubpaso = (idx: number) => onPatch(task, { subtasks: subtasks.filter((_, i) => i !== idx) });

  const venceCls = done
    ? 'text-emerald-600'
    : grupo === 'vencidas' ? 'text-red-500 font-medium' : grupo === 'hoy' ? 'text-amber-600 font-medium' : 'text-muted-foreground';

  return (
    <div className={`border-t border-border first:border-t-0 ${expanded ? 'bg-accent/30' : 'hover:bg-accent/40'} transition-colors`}>
      <div className="grid grid-cols-[28px_minmax(0,1fr)] items-start gap-x-2.5 px-3 py-2.5 md:grid-cols-[28px_minmax(0,1fr)_auto] md:items-center">
        <button
          type="button"
          onClick={() => onComplete(task, !done)}
          aria-label={done ? 'Reabrir' : 'Completar'}
          className={`mt-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border transition-colors md:mt-0 ${
            done ? 'border-foreground bg-foreground text-background' : 'border-muted-foreground/70 bg-background hover:border-foreground'
          }`}
        >
          {done && <CheckIcon className="h-3 w-3" strokeWidth={3} />}
        </button>

        <div className="min-w-0">
          <button type="button" onClick={() => onToggleExpand(task.id)} className="flex min-w-0 max-w-full items-center gap-2 text-left">
            <Tip label={pri.label}><span className={`h-2 w-2 shrink-0 rounded-full ${pri.cls}`} /></Tip>
            <span className={`truncate font-medium ${done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{task.title}</span>
          </button>
          {(task.linkedLabel || mentions.length > 0 || task.routineName || task.source?.startsWith('alert:')) && (
            <div className={`mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground ${done ? 'opacity-60' : ''}`}>
              {task.linkedLabel && (
                <button
                  type="button"
                  onClick={() => ruta && navigate(ruta)}
                  className={`inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-px ${ruta ? 'hover:bg-accent' : 'cursor-default'}`}
                >
                  {task.linkedLabel}
                  {ruta && <ArrowTopRightOnSquareIcon className="h-3 w-3" />}
                </button>
              )}
              {mentions.map((m) => {
                const Icono = ICONO_MENCION[m.type] || UserIcon;
                const r = rutaDeMencion(m);
                return (
                  <button
                    key={`${m.type}-${m.id}`}
                    type="button"
                    onClick={() => r && navigate(r)}
                    className={`inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-px ${r ? 'hover:bg-accent' : 'cursor-default'}`}
                  >
                    <Icono className="h-3 w-3" />{m.label}
                  </button>
                );
              })}
              {task.source?.startsWith('alert:') && <span>desde una alerta del tablero</span>}
              {task.routineName && <span>rutina {task.routineName}</span>}
            </div>
          )}
        </div>

        <div className="col-start-2 mt-1 flex items-center gap-2.5 md:col-start-auto md:mt-0">
          {vence && <span className={`whitespace-nowrap text-xs ${venceCls}`}>{vence}</span>}
          <Tip label={task.assigneeName || 'Sin asignar'}><span className="inline-flex"><Avatar id={task.assignedTo} name={task.assigneeName} /></span></Tip>
          <span className="hidden items-center gap-1 text-muted-foreground md:flex">
            {task.reminderAt && !done && (
              <Tip label={`Recordatorio ${new Date(task.reminderAt).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`}>
                <span className="inline-flex"><BellIcon className="h-4 w-4" /></span>
              </Tip>
            )}
            {task.attachmentUrl && (
              <Tip label="Tiene foto adjunta"><span className="inline-flex"><PaperClipIcon className="h-4 w-4" /></span></Tip>
            )}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" aria-label="Más acciones" className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
                <EllipsisVerticalIcon className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => onToggleExpand(task.id)}>{expanded ? 'Cerrar detalle' : 'Ver detalle'}</DropdownMenuItem>
              {puedeEditar && <DropdownMenuItem onClick={() => navigate(`/tasks/${task.id}/edit`)}>Editar completo</DropdownMenuItem>}
              {!done && puedeEditar && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onPostpone(task, 'manana')}>Posponer a mañana</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onPostpone(task, 'lunes')}>Posponer al lunes</DropdownMenuItem>
                </>
              )}
              {canAssign && assignees.length > 1 && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Reasignar a…</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
                    {assignees.map((a) => (
                      <DropdownMenuItem key={a.id} disabled={a.id === task.assignedTo} onClick={() => onPatch(task, { assignedTo: a.id })}>
                        <Avatar id={a.id} name={a.name} size="sm" /><span className="ml-2">{a.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
              {(canAssign || task.createdBy === currentUserId) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(task)}>Eliminar</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 pl-[52px] text-sm">
          {task.detail ? (
            <div className="mb-3 max-w-[62ch] text-muted-foreground">{renderDetalle(task.detail)}</div>
          ) : (
            <p className="mb-3 text-muted-foreground">Sin detalle.</p>
          )}

          <div className="mb-3 space-y-1.5">
            {subtasks.map((s: Subtask, idx: number) => (
              <div key={idx} className="group flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleSubpaso(idx)}
                  className={`flex h-[15px] w-[15px] items-center justify-center rounded border ${s.done ? 'border-foreground bg-foreground text-background' : 'border-muted-foreground/70'}`}
                  aria-label={s.done ? 'Desmarcar' : 'Marcar'}
                >
                  {s.done && <CheckIcon className="h-2.5 w-2.5" strokeWidth={3} />}
                </button>
                <span className={s.done ? 'text-muted-foreground line-through' : ''}>{s.text}</span>
                {puedeEditar && (
                  <button type="button" onClick={() => quitarSubpaso(idx)} className="ml-1 hidden rounded p-0.5 text-muted-foreground hover:text-foreground group-hover:inline-flex" aria-label="Quitar subpaso">
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
            {puedeEditar && !done && (
              <form onSubmit={(e) => { e.preventDefault(); agregarSubpaso(); }} className="flex items-center gap-2">
                <PlusIcon className="h-[15px] w-[15px] text-muted-foreground" />
                <input
                  value={nuevoSubpaso}
                  onChange={(e) => setNuevoSubpaso(e.target.value)}
                  placeholder="Agregar un paso y Enter"
                  className="w-full max-w-sm bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
                />
              </form>
            )}
          </div>

          <dl className="mb-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-xs">
            {task.dueAt && <><dt className="text-muted-foreground">Vence</dt><dd>{new Date(task.dueAt).toLocaleString('es-AR', { weekday: 'long', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</dd></>}
            {task.reminderAt && <><dt className="text-muted-foreground">Recordatorio</dt><dd>{new Date(task.reminderAt).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}{task.reminderSent ? ' · enviado' : ''}</dd></>}
            {task.attachmentUrl && <><dt className="text-muted-foreground">Adjunto</dt><dd><a href={getImageUrl(task.attachmentUrl)} target="_blank" rel="noreferrer" className="underline underline-offset-2">ver foto</a></dd></>}
            <dt className="text-muted-foreground">Creada</dt>
            <dd>{task.creatorName || 'Sistema'} · {new Date(task.createdAt).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })}</dd>
            {done && <><dt className="text-muted-foreground">Completada</dt><dd>{etiquetaCompletada(task, hoy) || 'sí'}</dd></>}
          </dl>

          <div className="flex flex-wrap gap-1.5">
            {ruta && task.linkedLabel && (
              <button type="button" onClick={() => navigate(ruta)} className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-background px-2.5 text-xs font-medium hover:bg-accent">
                Abrir {task.linkedLabel}<ArrowTopRightOnSquareIcon className="h-3 w-3" />
              </button>
            )}
            {!done && puedeEditar && (
              <button type="button" onClick={() => onPostpone(task, 'manana')} className="inline-flex h-7 items-center rounded-md border border-border bg-background px-2.5 text-xs font-medium hover:bg-accent">Posponer a mañana</button>
            )}
            {puedeEditar && (
              <button type="button" onClick={() => navigate(`/tasks/${task.id}/edit`)} className="inline-flex h-7 items-center rounded-md border border-border bg-background px-2.5 text-xs font-medium hover:bg-accent">Editar completo</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskRow;
